import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faExpand, faPencilAlt, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { DateHelper } from '@hub4b/helpers/date-helper';
import { NgxSpinnerModule } from 'ngx-spinner';
import { map, Observable } from 'rxjs';
import { ReminderActionType } from 'src/app/models/server/enums/reminder-action-type';
import { Folder } from 'src/app/models/server/folder';
import { Reminder } from 'src/app/models/server/reminder';
import { RemindersStateService } from 'src/app/services/state/reminders-state.service';

@Component({
    selector: 'app-reminders-section',
    templateUrl: './reminders-section.component.html',
    styleUrls: ['./reminders-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatTooltipModule, FaIconComponent, NgxSpinnerModule, AsyncPipe, DatePipe]
})
export class RemindersSectionComponent {
    /**
     * Références des icônes utilisés dans le template.
     */
    public deleteIcon = faTrash;
    public editIcon = faPencilAlt;
    public expandIcon = faExpand;
    public addIcon = faPlus;

    FRENCH_DATETIME_WITHOUT_SECONDS_FORMAT = DateHelper.FRENCH_DATETIME_WITHOUT_SECONDS_FORMAT;

    /**
     * Références des Enums utilisées dans le template.
     */
    ReminderActionType = ReminderActionType;

    /**
     * Liste des rappels du dossier.
     */
    @Input()
    public reminders$: Observable<Reminder[]>;

    /**
     * Dossier pour lequel les rappels sont affichés.
     */
    private _folder: Folder = null;

    /**
     * Spécifie le dossier pour lequel les rappels sont affichés.
     */
    @Input()
    public set folder(value: Folder) {
        this._folder = value;
    }

    /**
     * Obtient le dossier pour lequel les rappels sont affichés.
     */
    public get folder(): Folder {
        return this._folder;
    }

    /**
     * Valeur indiquant si le composant est affiché en mode consultation.
     */
    @Input()
    public readonly: boolean;

    /**
     * Emis lorsque l'utilisateur édite un rappel.
     */
    @Output()
    public reminderEdited: EventEmitter<Reminder> = new EventEmitter();

    /**
     * Emis lorsque l'utilisateur supprime un rappel.
     */
    @Output()
    public reminderDeleted: EventEmitter<Reminder> = new EventEmitter();

    /**
     * Constructeur.
     * @param remindersStateService
     */
    constructor(private remindersStateService: RemindersStateService) {
        this.reminders$ = this.remindersStateService.remindersListChange$.pipe(
            map((x) => {
                x?.forEach((reminder) => {
                    reminder.intervalRepeat = Reminder.formatTime(reminder.occurenceFrequency);
                });
                return x;
            })
        );
    }

    /**
     * Emit lorsque l'utilisateur clique sur l'icone d'édition d'un rappel.
     */
    public editReminder(reminder: Reminder): void {
        this.reminderEdited.emit(reminder);
    }

    /**
     * Emit lorsque l'utilisateur clique sur l'icone de suppression d'un rappel.
     */
    public deleteReminder(reminder: Reminder): void {
        this.reminderDeleted.emit(reminder);
    }
}
